// Phase 1 Integration Test
// Test tất cả services: StudyHistoryService, QuizHistoryService, AchievementService

import { studyHistoryService } from '../StudyHistoryService';
import { quizHistoryService } from '../QuizHistoryService';
import { achievementService } from '../AchievementService';
import { sqliteDatabaseService } from '../../database/SQLiteDatabaseService';

/**
 * Test Suite for Phase 1: Core Services
 * 
 * Chạy test này để verify tất cả functionality của Phase 1
 */
class Phase1IntegrationTest {
  private testUserId = 'test-user-' + Date.now();
  private testResults: { test: string; passed: boolean; error?: string }[] = [];

  async runAllTests(): Promise<void> {
    console.log('\n🧪 ==================== PHASE 1 INTEGRATION TEST ====================\n');

    try {
      await this.testDatabaseInitialization();
      await this.testStudyHistoryService();
      await this.testQuizHistoryService();
      await this.testAchievementService();
      await this.testIntegrationScenario();

      this.printResults();
    } catch (error) {
      console.error('❌ Test suite failed:', error);
    }
  }

  // ==================== TEST: DATABASE INITIALIZATION ====================

  async testDatabaseInitialization(): Promise<void> {
    console.log('\n📦 Testing Database Initialization...\n');

    try {
      // Test: Database should initialize
      console.log('🔧 Initializing SQLite database...');
      await sqliteDatabaseService.initialize();
      
      // Wait a bit for initialization to complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Check if initialized
      const isReady = (sqliteDatabaseService as any).isInitialized;
      console.log('📊 Database initialized:', isReady);
      
      this.recordTest('Database initialization', true);

      // Test: Create test user
      console.log('👤 Creating test user...');
      const user = await sqliteDatabaseService.createUser({
        email: `test-${this.testUserId}@example.com`,
        name: 'Test User',
        role: 'student'
      });

      this.testUserId = user.id;
      this.recordTest('Create test user', true);
      console.log('✅ Test user created:', user.id);

    } catch (error: any) {
      console.error('❌ Database initialization failed:', error);
      this.recordTest('Database initialization', false, error.message);
      throw error; // Stop tests if database fails
    }
  }

  // ==================== TEST: STUDY HISTORY SERVICE ====================

  async testStudyHistoryService(): Promise<void> {
    console.log('\n📚 Testing StudyHistoryService...\n');

    try {
      // Test: Start a lesson
      const lesson1 = await studyHistoryService.startLesson(
        this.testUserId,
        'L12_C4_B01',
        'Dao động điều hòa'
      );

      this.recordTest('Start lesson', lesson1 !== null);
      console.log('✅ Lesson started:', lesson1.lessonId);

      // Test: Update lesson progress
      await studyHistoryService.updateLessonProgress(
        this.testUserId,
        'L12_C4_B01',
        50,
        300
      );

      this.recordTest('Update lesson progress', true);
      console.log('✅ Lesson progress updated: 50%');

      // Test: Get lesson progress
      const progress = await studyHistoryService.getLessonProgress(
        this.testUserId,
        'L12_C4_B01'
      );

      const progressCorrect = progress !== null && progress.progress === 50;
      this.recordTest('Get lesson progress', progressCorrect);
      console.log('✅ Lesson progress retrieved:', progress?.progress);

      // Test: Complete lesson
      await studyHistoryService.completeLesson(this.testUserId, 'L12_C4_B01');
      
      const completedProgress = await studyHistoryService.getLessonProgress(
        this.testUserId,
        'L12_C4_B01'
      );

      const isCompleted = completedProgress?.status === 'completed';
      this.recordTest('Complete lesson', isCompleted);
      console.log('✅ Lesson completed');

      // Test: Add bookmark
      await studyHistoryService.addBookmark(this.testUserId, 'L12_C4_B01', 'section-1');
      
      const progressWithBookmark = await studyHistoryService.getLessonProgress(
        this.testUserId,
        'L12_C4_B01'
      );

      const hasBookmark = progressWithBookmark?.bookmarks?.includes('section-1');
      this.recordTest('Add bookmark', hasBookmark === true);
      console.log('✅ Bookmark added');

      // Test: Study sessions
      const sessionId = await studyHistoryService.startSession(
        this.testUserId,
        'lesson',
        'L12_C4_B01'
      );

      this.recordTest('Start study session', sessionId !== null);
      console.log('✅ Study session started:', sessionId);

      await studyHistoryService.endSession(sessionId, 600);
      this.recordTest('End study session', true);
      console.log('✅ Study session ended');

      // Test: Get statistics
      const stats = await studyHistoryService.getUserStatistics(this.testUserId);
      
      const statsValid = stats.totalLessons > 0 && stats.completedLessons > 0;
      this.recordTest('Get user statistics', statsValid);
      console.log('✅ User statistics:', stats);

    } catch (error: any) {
      this.recordTest('StudyHistoryService', false, error.message);
      console.error('❌ StudyHistoryService test failed:', error);
    }
  }

  // ==================== TEST: QUIZ HISTORY SERVICE ====================

  async testQuizHistoryService(): Promise<void> {
    console.log('\n📝 Testing QuizHistoryService...\n');

    try {
      // Test: Save quiz result - Attempt 1
      const quizResult1 = await quizHistoryService.saveQuizResult({
        userId: this.testUserId,
        quizId: 'quiz-L12-C4-B01',
        lessonId: 'L12_C4_B01',
        quizTitle: 'Trắc nghiệm Dao động điều hòa',
        quizType: 'lesson',
        score: 75,
        totalQuestions: 10,
        correctAnswers: 7,
        wrongAnswers: 3,
        skippedAnswers: 0,
        timeSpent: 600,
        timeLimitSeconds: 900,
        passed: true,
        passingScore: 70,
        answers: [
          {
            questionId: 'q1',
            questionText: 'Câu hỏi test',
            questionType: 'TN',
            userAnswer: 'A',
            correctAnswer: 'A',
            isCorrect: true,
            timeSpent: 60
          }
        ]
      });

      this.recordTest('Save quiz result (attempt 1)', quizResult1 !== null);
      console.log('✅ Quiz result 1 saved:', quizResult1.id);

      // Test: Save quiz result - Attempt 2 (improvement)
      const quizResult2 = await quizHistoryService.saveQuizResult({
        userId: this.testUserId,
        quizId: 'quiz-L12-C4-B01',
        lessonId: 'L12_C4_B01',
        quizTitle: 'Trắc nghiệm Dao động điều hòa',
        quizType: 'lesson',
        score: 90,
        totalQuestions: 10,
        correctAnswers: 9,
        wrongAnswers: 1,
        skippedAnswers: 0,
        timeSpent: 500,
        passed: true,
        passingScore: 70,
        answers: []
      });

      this.recordTest('Save quiz result (attempt 2)', quizResult2.attemptNumber === 2);
      console.log('✅ Quiz result 2 saved: Attempt #', quizResult2.attemptNumber);

      // Test: Get quiz history
      const history = await quizHistoryService.getQuizHistory(this.testUserId);
      
      this.recordTest('Get quiz history', history.length === 2);
      console.log('✅ Quiz history retrieved:', history.length, 'results');

      // Test: Get latest attempt
      const latest = await quizHistoryService.getLatestAttempt(
        this.testUserId,
        'quiz-L12-C4-B01'
      );

      const isLatest = latest?.attemptNumber === 2 && latest?.score === 90;
      this.recordTest('Get latest attempt', isLatest);
      console.log('✅ Latest attempt:', latest?.attemptNumber, 'Score:', latest?.score);

      // Test: Get all attempts
      const allAttempts = await quizHistoryService.getAllAttempts(
        this.testUserId,
        'quiz-L12-C4-B01'
      );

      this.recordTest('Get all attempts', allAttempts.length === 2);
      console.log('✅ All attempts:', allAttempts.length);

      // Test: Get quiz statistics
      const quizStats = await quizHistoryService.getQuizStatistics(this.testUserId);
      
      const statsValid = 
        quizStats.totalQuizzes === 2 &&
        quizStats.averageScore === 82.5 && // (75 + 90) / 2
        quizStats.passRate === 100;

      this.recordTest('Get quiz statistics', statsValid);
      console.log('✅ Quiz statistics:', quizStats);

      // Test: Get improvement trend
      const trend = await quizHistoryService.getImprovementTrend(
        this.testUserId,
        'L12_C4_B01'
      );

      const trendValid = trend.trend === 'improving' && trend.improvement === 15;
      this.recordTest('Get improvement trend', trendValid);
      console.log('✅ Improvement trend:', trend.trend, '+', trend.improvement);

      // Test: Compare attempts
      const comparison = await quizHistoryService.compareAttempts(
        this.testUserId,
        'quiz-L12-C4-B01'
      );

      const comparisonValid = 
        comparison.trend === 'improving' &&
        comparison.bestScore === 90 &&
        comparison.latestScore === 90;

      this.recordTest('Compare attempts', comparisonValid);
      console.log('✅ Attempts comparison:', comparison);

    } catch (error: any) {
      this.recordTest('QuizHistoryService', false, error.message);
      console.error('❌ QuizHistoryService test failed:', error);
    }
  }

  // ==================== TEST: ACHIEVEMENT SERVICE ====================

  async testAchievementService(): Promise<void> {
    console.log('\n🏆 Testing AchievementService...\n');

    try {
      // Test: Check after quiz complete (should award first_quiz, improver, etc)
      const newAchievements = await achievementService.checkAfterQuizComplete(
        this.testUserId,
        'quiz-L12-C4-B01',
        100, // Perfect score
        400,
        900,
        true
      );

      this.recordTest('Check and award achievements', newAchievements.length > 0);
      console.log('✅ Awarded achievements:', newAchievements.length);
      newAchievements.forEach(a => {
        console.log(`   🏆 ${a.title} - ${a.description}`);
      });

      // Test: Get user achievements
      const userAchievements = await achievementService.getUserAchievements(this.testUserId);
      
      this.recordTest('Get user achievements', userAchievements.length > 0);
      console.log('✅ Total achievements:', userAchievements.length);

      // Test: Get recent achievements
      const recentAchievements = await achievementService.getRecentAchievements(
        this.testUserId,
        5
      );

      this.recordTest('Get recent achievements', recentAchievements.length > 0);
      console.log('✅ Recent achievements:', recentAchievements.length);

      // Test: Check if has specific achievement
      const hasFirstQuiz = await achievementService.hasAchievement(
        this.testUserId,
        'first_quiz'
      );

      this.recordTest('Has specific achievement', hasFirstQuiz === true);
      console.log('✅ Has first_quiz achievement:', hasFirstQuiz);

      // Test: Get all achievement definitions
      const allDefinitions = achievementService.getAllAchievementDefinitions();
      
      this.recordTest('Get achievement definitions', allDefinitions.length >= 10);
      console.log('✅ Total achievement definitions:', allDefinitions.length);

    } catch (error: any) {
      this.recordTest('AchievementService', false, error.message);
      console.error('❌ AchievementService test failed:', error);
    }
  }

  // ==================== TEST: INTEGRATION SCENARIO ====================

  async testIntegrationScenario(): Promise<void> {
    console.log('\n🔄 Testing Full Integration Scenario...\n');

    try {
      console.log('Scenario: User học bài → làm trắc nghiệm → nhận thành tích\n');

      // 1. User starts a new lesson
      console.log('1️⃣ User bắt đầu học bài...');
      const lesson = await studyHistoryService.startLesson(
        this.testUserId,
        'L12_C4_B02',
        'Sóng cơ'
      );

      // 2. User studies for some time
      console.log('2️⃣ User đang học (30 phút)...');
      await new Promise(resolve => setTimeout(resolve, 100)); // Simulate time
      await studyHistoryService.updateLessonProgress(
        this.testUserId,
        'L12_C4_B02',
        75,
        1800
      );

      // 3. User completes the lesson
      console.log('3️⃣ User hoàn thành bài học...');
      await studyHistoryService.completeLesson(this.testUserId, 'L12_C4_B02');
      
      // Check for lesson_complete achievement
      const achievements1 = await achievementService.checkAfterLessonComplete(
        this.testUserId,
        'L12_C4_B02',
        1800
      );

      if (achievements1.length > 0) {
        console.log(`   🎉 Nhận được ${achievements1.length} thành tích!`);
      }

      // 4. User takes a quiz
      console.log('4️⃣ User làm trắc nghiệm...');
      const quizResult = await quizHistoryService.saveQuizResult({
        userId: this.testUserId,
        quizId: 'quiz-L12-C4-B02',
        lessonId: 'L12_C4_B02',
        quizTitle: 'Trắc nghiệm Sóng cơ',
        quizType: 'lesson',
        score: 95,
        totalQuestions: 20,
        correctAnswers: 19,
        wrongAnswers: 1,
        skippedAnswers: 0,
        timeSpent: 720,
        timeLimitSeconds: 1200,
        passed: true,
        passingScore: 70,
        answers: []
      });

      console.log(`   ✅ Hoàn thành với điểm: ${quizResult.score}/100`);

      // 5. Check for achievements
      console.log('5️⃣ Kiểm tra thành tích...');
      const achievements2 = await achievementService.checkAfterQuizComplete(
        this.testUserId,
        'quiz-L12-C4-B02',
        95,
        720,
        1200
      );

      if (achievements2.length > 0) {
        console.log(`   🎉 Nhận được ${achievements2.length} thành tích mới!`);
        achievements2.forEach(a => {
          console.log(`      🏆 ${a.title}`);
        });
      }

      // 6. Get final statistics
      console.log('6️⃣ Thống kê cuối cùng...');
      const finalStats = await studyHistoryService.getUserStatistics(this.testUserId);
      const quizStats = await quizHistoryService.getQuizStatistics(this.testUserId);
      const allAchievements = await achievementService.getUserAchievements(this.testUserId);

      console.log('\n📊 TỔNG KẾT:');
      console.log(`   📚 Bài học hoàn thành: ${finalStats.completedLessons}/${finalStats.totalLessons}`);
      console.log(`   📝 Trắc nghiệm: ${quizStats.totalQuizzes} bài`);
      console.log(`   ⭐ Điểm trung bình: ${quizStats.averageScore.toFixed(1)}/100`);
      console.log(`   🏆 Thành tích: ${allAchievements.length} huy hiệu`);
      console.log(`   ⏱️ Thời gian học: ${Math.floor(finalStats.totalStudyTime / 60)} phút`);

      this.recordTest('Full integration scenario', true);

    } catch (error: any) {
      this.recordTest('Integration scenario', false, error.message);
      console.error('❌ Integration scenario failed:', error);
    }
  }

  // ==================== UTILITIES ====================

  private recordTest(testName: string, passed: boolean, error?: string): void {
    this.testResults.push({ test: testName, passed, error });
    
    if (passed) {
      console.log(`✅ PASS: ${testName}`);
    } else {
      console.log(`❌ FAIL: ${testName}`);
      if (error) {
        console.log(`   Error: ${error}`);
      }
    }
  }

  private printResults(): void {
    console.log('\n\n📊 ==================== TEST RESULTS ====================\n');

    const totalTests = this.testResults.length;
    const passedTests = this.testResults.filter(r => r.passed).length;
    const failedTests = totalTests - passedTests;

    console.log(`Total Tests: ${totalTests}`);
    console.log(`✅ Passed: ${passedTests}`);
    console.log(`❌ Failed: ${failedTests}`);
    console.log(`Success Rate: ${((passedTests / totalTests) * 100).toFixed(1)}%`);

    if (failedTests > 0) {
      console.log('\n❌ Failed Tests:');
      this.testResults.filter(r => !r.passed).forEach(r => {
        console.log(`   - ${r.test}${r.error ? ': ' + r.error : ''}`);
      });
    }

    console.log('\n=======================================================\n');

    if (passedTests === totalTests) {
      console.log('🎉 ALL TESTS PASSED! Phase 1 is ready for production.\n');
    } else {
      console.log('⚠️  Some tests failed. Please review and fix issues.\n');
    }
  }
}

// Export for manual testing
export const phase1Test = new Phase1IntegrationTest();

// Auto-run if this file is executed directly
if (typeof window !== 'undefined' && (window as any).__RUN_PHASE1_TEST__) {
  phase1Test.runAllTests();
}

